import { gql, useApolloClient } from "@apollo/client";
import React, { createContext, ReactNode, useContext } from "react";
import {
  matchPath,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useOrganization } from "src/hooks/useOrganization";
import * as Url from "src/services/url/OrgAdmin";
import * as GQL from "src/types/graphql";
import * as RD from "src/types/remoteData";
import { useEnrollmentPeriod } from "./EnrollmentPeriodProvider";

interface State {
  navFormTemplates: RD.RemoteData<
    unknown,
    GQL.GetFormTemplatesByForLeftNavigation_form_template[]
  >;
}
interface FormTemplateContextType extends State {
  selectedNavFormTemplate:
    | GQL.GetFormTemplatesByForLeftNavigation_form_template
    | undefined;
}

const FormTemplateContext = createContext<FormTemplateContextType>({
  navFormTemplates: RD.notAsked(),
  selectedNavFormTemplate: undefined,
});

const initialState: State = {
  navFormTemplates: RD.notAsked(),
};
interface FormTemplateProviderProps {
  children: ReactNode;
}
export function FormTemplateProvider(props: FormTemplateProviderProps) {
  const { children } = props;
  const [state, setState] = React.useState(initialState);
  const navigate = useNavigate();
  const organization = useOrganization();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { formTemplateId = "" } = useParams();
  const { selectedEnrollmentPeriod } = useEnrollmentPeriod();

  const client = useApolloClient();

  const navigateAfterFetching = React.useCallback(
    (
      formTemplates: RD.RemoteData<
        unknown,
        GQL.GetFormTemplatesByForLeftNavigation_form_template[]
      >
    ) => {
      if (!formTemplates.hasData() || !organization.hasData()) {
        return;
      }

      if (!matchPath(Url.Forms.indexPath, location.pathname)) {
        // User is currently not in any form pages. We don't need to navigate away
        return;
      }

      // Figure out if we need to navigate away from the current form page after changing enrollment period.
      const firstFormTemplate = formTemplates.data[0];

      if (firstFormTemplate === undefined) {
        // enrollment period doesn't have forms. Navigate to default page of the org.
        navigate(Url.Enrollments.index(organization.data));
      } else {
        const formTemplateUrls = formTemplates.data.map((ft) =>
          Url.Forms.index({
            organization: organization.data,
            formTemplateId: ft.id,
          })
        );

        // Check if we're already on the form page for the current enrollment period.
        if (formTemplateUrls.includes(location.pathname)) {
          // we're already in this enrollment periods form pages. Don't need to navigate away.
          return;
        }

        // We're in the form page belong to a different enrollment period.
        // Navigate to the first form page for the current enrollment period.
        navigate(
          Url.Forms.index({
            organization: organization.data,
            formTemplateId: firstFormTemplate.id,
            params: searchParams.toString(),
          })
        );
      }
    },
    [location.pathname, navigate, organization, searchParams]
  );

  const fetchFormTemplates = React.useCallback(
    async (enrollmentPeriodId: uuid) => {
      const remoteData = RD.fromApolloResult(
        await client.query<
          GQL.GetFormTemplatesByForLeftNavigation,
          GQL.GetFormTemplatesByForLeftNavigationVariables
        >({
          query: GET_FORM_TEMPLATES_FOR_LEFT_NAVIGATION,
          variables: {
            enrollment_period: enrollmentPeriodId,
          },
        })
      );
      const formTemplates = remoteData.map((data) => data.form_template);

      setState({ ...state, navFormTemplates: formTemplates });
      navigateAfterFetching(formTemplates);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigateAfterFetching, client]
  );

  React.useEffect(() => {
    if (selectedEnrollmentPeriod?.id) {
      fetchFormTemplates(selectedEnrollmentPeriod.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEnrollmentPeriod]);

  const selectedNavFormTemplate = React.useMemo(() => {
    return state.navFormTemplates
      .map((formTemplates) =>
        formTemplates.find((formTemplate) => formTemplate.id === formTemplateId)
      )
      .withDefault(undefined);
  }, [formTemplateId, state.navFormTemplates]);

  return (
    <FormTemplateContext.Provider
      value={{
        ...state,
        selectedNavFormTemplate,
      }}
    >
      {children}
    </FormTemplateContext.Provider>
  );
}

export function useFormTemplates() {
  const context = useContext(FormTemplateContext);
  if (context === undefined) {
    throw new Error(
      "useFormTemplates must be used within a FormTemplateContext"
    );
  }
  return context;
}

export const GET_FORM_TEMPLATES_FOR_LEFT_NAVIGATION = gql`
  query GetFormTemplatesByForLeftNavigation($enrollment_period: uuid!) {
    form_template(
      where: {
        enrollment_period_id: { _eq: $enrollment_period }
        deleted_at: { _is_null: true }
        mode: { _eq: Published }
      }
      order_by: { created_at: asc }
    ) {
      id
      name
      description
      enrollment_period_id
      enrollment_period {
        id
        name
      }
      active
      lottery_offers_enabled
      offer_start_at
      locked
      mode
      sections {
        schools_ranking_section {
          ranking_enabled
        }
      }
      form_verifications {
        id
      }
    }
  }
`;
